// Synthèse du dossier : état de l'analyse, pièces absentes / illisibles, décomptes réels, questions à poser, prochaines actions, assistance.
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Play, RefreshCw, Wand2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { dateHeure, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Chargement, ChampChoix, ChampTexte, EtatErreur, IndicateurEnregistrement, Panneau, Progression, useNotifier,
  type EtatEnregistrement,
} from '../../components/ui.tsx';
import { BadgeStatutExigence, BadgeStatutTache, STATUTS_DOSSIER, STATUTS_EXIGENCE } from '../../components/statuts.tsx';
import { DossierCadre, useDossier } from './DossierCadre.tsx';
import { DESTINATAIRES, NATURES, TYPES_ECART, traitementEnCours, useSynthese } from './commun.tsx';

interface ResultatToutPreparer {
  devis: { id: string; number: string; cree: boolean }; revision_id: string; ajoutes: number; lignes: number; deja: number; elements: number;
  a_completer: { total: number; quantite_a_saisir: number; sans_ouvrage: number; ecarts: number };
}

export function PageSynthese() {
  const { id = '' } = useParams();
  const { peut } = useSession();
  const s = useSynthese(id);
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [lancement, setLancement] = useState(false);
  const [preparation, setPreparation] = useState(false);
  const nav = useNavigate();
  const enCours = traitementEnCours(s.data);
  const etaitEnCours = useRef(false);

  // À la fin d'un traitement : rafraîchir l'en-tête du dossier et les listes.
  useEffect(() => {
    if (etaitEnCours.current && !enCours) {
      qc.invalidateQueries({ queryKey: ['dossier', id] });
      qc.invalidateQueries({ queryKey: ['etudes-exigences', id] });
      qc.invalidateQueries({ queryKey: ['etudes-ecarts', id] });
    }
    etaitEnCours.current = enCours;
  }, [enCours]);

  const lancer = async (forcer: boolean) => {
    setLancement(true);
    try {
      const r = await api.post<{ nouvelle: boolean; piecesEnCours: number }>(`/api/dossiers/${id}/analyser`, { forcer });
      if (!r.nouvelle) notifier(forcer ? 'Une analyse vient déjà d’être lancée : elle est en cours ou terminée.' : 'L’analyse est déjà à jour pour ces pièces.');
      else notifier(r.piecesEnCours ? 'Analyse en file : elle tiendra compte des pièces encore en lecture si elles sont prêtes.' : 'Analyse lancée.');
      await s.refetch();
    } catch (e) { notifier(messageErreur(e), true); } finally { setLancement(false); }
  };

  // « Tout préparer » : devis brouillon repris ou créé, éléments du chiffrage proposé cochés d'office ajoutés, puis l'estimation
  // de pose (durée, équipe, matériel, montant) avec ce qui reste à compléter. Rien n'est envoyé au client.
  const toutPreparer = async () => {
    setPreparation(true);
    try {
      const r = await api.post<ResultatToutPreparer>(`/api/dossiers/${id}/tout-preparer`, {});
      qc.invalidateQueries({ queryKey: ['devis-dossier', id] });
      const q = new URLSearchParams({ revision: r.revision_id, prepare: '1', aj: String(r.ajoutes), dj: String(r.deja), ac: String(r.a_completer.total),
        qs: String(r.a_completer.quantite_a_saisir), ec: String(r.a_completer.ecarts), cree: r.devis.cree ? '1' : '0' });
      nav(`/etudes/${id}/devis/${r.devis.id}/estimation?${q}`);
    } catch (e) { notifier(messageErreur(e), true); setPreparation(false); }
  };

  const t = s.data?.analyse.tache;
  // Dossier classé sans suite : lecture seule, pas d'analyse à relancer (le bandeau propose de le rouvrir).
  const classe = !!useDossier(id).data?.classement;
  const preparable = peut('devis.ecrire') && peut('devis.couts') && !classe && t?.status === 'termine';
  const action = peut('dossier.ecrire') && !classe && s.data && (
    <>
      {t ? <Bouton variante={s.data.analyse.aJour ? 'secondaire' : 'principal'} icone={<RefreshCw aria-hidden />} chargement={lancement} disabled={enCours} onClick={() => lancer(!!s.data?.analyse.aJour)}>Relancer l’analyse</Bouton>
        : <Bouton variante="principal" icone={<Play aria-hidden />} chargement={lancement} disabled={enCours || !s.data.pieces.total} onClick={() => lancer(false)}>Lancer l’analyse</Bouton>}
      {preparable && <Bouton variante={s.data.analyse.aJour ? 'principal' : 'secondaire'} icone={<Wand2 aria-hidden />} chargement={preparation} disabled={enCours} onClick={toutPreparer}>Tout préparer</Bouton>}
    </>
  );

  return (
    <DossierCadre dossierId={id} actif="synthese" actions={action || undefined}>
      {s.isLoading && <Chargement lignes={6} />}
      {s.isError && <EtatErreur erreur={s.error} reessayer={() => s.refetch()} />}
      {s.data && (() => {
        const x = s.data;
        const res = t?.result ?? null;
        const statutsExigence = Object.keys(STATUTS_EXIGENCE);
        return (
          <div className="pile-l">
            {(x.pieces.absentes.length > 0 || x.pieces.illisibles.length > 0) && (
              <Alerte ton="danger" titre="Pièces absentes ou illisibles : l’analyse est incomplète"
                action={<Link className="btn btn-compact" to={`/etudes/${id}/pieces`}>Voir les pièces</Link>}>
                <ul className="et-liste-simple">
                  {x.pieces.absentes.map((a) => <li key={a.cle}><strong>{a.titre}</strong> — {a.description}</li>)}
                  {x.pieces.illisibles.map((i) => <li key={i.versionId}><strong>{i.titre}</strong> — {i.description}</li>)}
                </ul>
              </Alerte>
            )}
            <div className="et-synthese-grille">
              <Panneau titre="Analyse des pièces">
                <div className="pile">
                  {x.analyse.piecesEnCours > 0 && (
                    <Alerte ton="info" titre={`${pluriel(x.analyse.piecesEnCours, 'pièce')} en cours de lecture`}>L’analyse démarrera automatiquement quand toutes les pièces seront lues.</Alerte>
                  )}
                  {!t && x.analyse.piecesEnCours === 0 && (
                    <p className="secondaire">{x.pieces.total ? 'Aucune analyse n’a encore été faite sur ces pièces.' : 'Déposez les pièces du dossier : leur analyse démarre automatiquement après lecture.'}</p>
                  )}
                  {t && (
                    <>
                      <div className="rangee" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <BadgeStatutTache statut={t.status} />
                        {t.status === 'termine' && (x.analyse.aJour ? <Badge ton="succes">À jour avec les pièces</Badge> : <Badge ton="ambre">Pièces ou demande modifiées depuis</Badge>)}
                        <span className="petit discret">{t.finished_at ? `Terminée le ${dateHeure(t.finished_at)}` : `Lancée le ${dateHeure(t.created_at)}`}</span>
                      </div>
                      {['en_file', 'en_cours'].includes(t.status) && <Progression pct={t.progress} libelle={t.progress_label ?? 'En file d’attente'} />}
                      {t.status === 'echec' && <Alerte ton="danger" titre="L’analyse a échoué">{t.error ?? 'Erreur inconnue.'} Les pièces et les exigences existantes sont conservées.</Alerte>}
                      {res && t.status === 'termine' && (
                        <p className="petit secondaire">{pluriel(res.pieces, 'pièce')} et {pluriel(res.extraits, 'extrait')} lus par {x.analyse.lexique} règles métier explicites.</p>
                      )}
                    </>
                  )}
                </div>
              </Panneau>
              <Panneau titre="Assistance">
                <EtatAssistance ia={x.ia} resultat={res} />
              </Panneau>
            </div>

            <div className="et-synthese-grille">
              <Panneau titre={`Exigences (${x.exigences.total})`} actions={<Link className="btn btn-compact btn-fantome" to={`/etudes/${id}/exigences`}>Ouvrir</Link>}>
                {x.exigences.total === 0 ? <p className="secondaire">Aucune exigence pour l’instant.</p> : (
                  <div className="pile">
                    <ul className="et-decompte" aria-label="Exigences par statut">
                      {statutsExigence.map((k) => (
                        <li key={k}><Link to={`/etudes/${id}/exigences?statut=${k}`}><BadgeStatutExigence statut={k} /><span className="et-nombre">{x.exigences.parStatut[k] ?? 0}</span></Link></li>
                      ))}
                    </ul>
                    <dl className="et-chiffres">
                      <div><dt>Absentes du chiffrage</dt><dd>{x.exigences.nonChiffrees}</dd></div>
                      <div><dt>Absentes de la demande</dt><dd>{x.exigences.horsDemande}</dd></div>
                      <div><dt>À vérifier (incertaines)</dt><dd>{x.exigences.incertaines}</dd></div>
                      {Object.entries(NATURES).map(([k, v]) => <div key={k}><dt>{v.libelle}s</dt><dd>{x.exigences.parNature[k] ?? 0}</dd></div>)}
                    </dl>
                  </div>
                )}
              </Panneau>
              <Panneau titre="Écarts" actions={<Link className="btn btn-compact btn-fantome" to={`/etudes/${id}/ecarts`}>Ouvrir</Link>}>
                {Object.keys(x.ecarts).length === 0 ? <p className="secondaire">Aucun écart relevé.</p> : (
                  <ul className="et-decompte" aria-label="Écarts par type">
                    {Object.entries(TYPES_ECART).filter(([k]) => x.ecarts[k]).map(([k, v]) => (
                      <li key={k}><Link to={`/etudes/${id}/ecarts`}><Badge ton={v.ton}>{v.pluriel}</Badge>
                        <span className="et-nombre" aria-label={`${x.ecarts[k].ouverts} ouvert${x.ecarts[k].ouverts > 1 ? 's' : ''} sur ${x.ecarts[k].total}`}>{x.ecarts[k].ouverts}<span className="tres-petit discret"> / {x.ecarts[k].total}</span></span></Link></li>
                    ))}
                  </ul>
                )}
              </Panneau>
            </div>

            <div className="et-synthese-grille">
              <Panneau titre={`Questions à poser (${x.questions.parStatut.a_poser ?? 0})`} actions={<Link className="btn btn-compact btn-fantome" to={`/etudes/${id}/ecarts?onglet=questions`}>Toutes</Link>}>
                {x.questions.aPoser.length === 0 ? <p className="secondaire">Aucune question en attente.</p> : (
                  <ul className="et-liste-simple">
                    {x.questions.aPoser.map((q) => <li key={q.id}><span className="tres-petit discret">{DESTINATAIRES[q.addressee ?? ''] ?? 'Destinataire à préciser'} — </span>{q.text}</li>)}
                  </ul>
                )}
              </Panneau>
              <Panneau titre="Prochaines actions">
                {x.prochainesActions.length === 0 ? <p className="secondaire">Rien en attente.</p> : (
                  <ul className="et-actions">
                    {x.prochainesActions.map((a, i) => <li key={i}><Link to={`/etudes/${id}/${a.lien}`}>{a.texte}</Link></li>)}
                  </ul>
                )}
              </Panneau>
            </div>
            <PanneauDossier dossierId={id} />
          </div>
        );
      })()}
    </DossierCadre>
  );
}

function EtatAssistance({ ia, resultat }: { ia: { disponible: boolean; documentsClientsAutorises: boolean; local: boolean; raison: string | null }; resultat: any }) {
  if (!ia.disponible) return (
    <div className="pile">
      <span className="et-etat-badge"><Badge ton="neutre">Non configurée</Badge></span>
      <p className="petit secondaire">L’analyse repose uniquement sur les règles métier explicites (aucun extrait n’est envoyé à un service externe). {ia.raison}</p>
    </div>
  );
  if (!ia.documentsClientsAutorises) return (
    <div className="pile">
      <span className="et-etat-badge"><Badge ton="ambre">Configurée, envoi des pièces non autorisé</Badge></span>
      <p className="petit secondaire">Les pièces clients ne sont pas transmises au service d’assistance : seules les règles métier sont appliquées.</p>
    </div>
  );
  return (
    <div className="pile">
      <span className="et-etat-badge"><Badge ton="info">Disponible{ia.local ? ' (modèle local)' : ''}</Badge></span>
      <p className="petit secondaire">Une passe complémentaire propose des exigences, toujours marquées « à vérifier » et rejetées sans extrait source valide.</p>
      {resultat?.ia === 'faite' && <p className="petit">{pluriel(resultat.iaExigences ?? 0, 'proposition retenue', 'propositions retenues')}, {pluriel(resultat.iaRejetees ?? 0, 'rejetée', 'rejetées')} faute de source.</p>}
      {resultat?.ia === 'erreur' && <Alerte ton="ambre">Le service d’assistance n’a pas répondu : l’analyse par règles est complète.</Alerte>}
    </div>
  );
}

function PanneauDossier({ dossierId }: { dossierId: string }) {
  const { peut } = useSession();
  const q = useDossier(dossierId);
  const qc = useQueryClient();
  const [demande, setDemande] = useState<string | null>(null);
  const [etat, setEtat] = useState<EtatEnregistrement>('enregistre');
  const minuterie = useRef<ReturnType<typeof setTimeout>>(undefined);
  const x: any = q.data;
  if (!x) return null;
  const ecrire = peut('dossier.ecrire');
  const valeur = demande ?? x.clientRequest ?? '';
  const envoyer = async (corps: Record<string, unknown>) => {
    setEtat('en_cours');
    try {
      await api.patch(`/api/dossiers/${dossierId}`, corps);
      setEtat('enregistre');
      await qc.invalidateQueries({ queryKey: ['dossier', dossierId] });
      await qc.invalidateQueries({ queryKey: ['etudes-synthese', dossierId] });
    } catch { setEtat('erreur'); }
  };
  return (
    <Panneau titre="Dossier" actions={ecrire ? <IndicateurEnregistrement etat={etat} /> : undefined}>
      <div className="pile">
        <ChampChoix libelle="Statut du dossier" value={x.status} disabled={!ecrire}
          options={Object.entries(STATUTS_DOSSIER).map(([valeur, v]) => ({ valeur, libelle: v.libelle }))}
          onChange={(e) => envoyer({ status: e.target.value })} />
        <ChampTexte libelle="Demande du client" value={valeur} readOnly={!ecrire} rows={4}
          aide="Modifier la demande rend l’analyse « à relancer » : les drapeaux « Demandé » en dépendent."
          onChange={(e) => {
            setDemande(e.target.value); setEtat('modifie');
            clearTimeout(minuterie.current);
            const v = e.target.value;
            minuterie.current = setTimeout(() => envoyer({ clientRequest: v || null }), 800);
          }} />
      </div>
    </Panneau>
  );
}
